import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useOutletContext } from 'react-router'
import { useTranslation } from 'react-i18next'
import { addToast, Button, Chip } from '@structural-climbing/ui'
import { useMyClasses } from '../features/classes/useMyClasses'
import type { ClassPerson, MemberClass } from '../features/classes/types'
import { Page } from '../design/Page'
import { Section } from '../design/Section'
import { SurfaceCard } from '../design/SurfaceCard'
import { ChipTabs } from '../design/ChipTabs'
import { EmptyState } from '../design/EmptyState'
import { SkeletonCard } from '../design/Skeleton'
import { staggerIndex } from '../design/motion'
import type { GymContext } from '../layouts/GymLayout'

/** Tailwind needs the full class names in the source to keep them. */
/** Fixed hues, the same the gym picks from; never the gym's theme slots. */
const COLOR_BAR: Record<string, string> = {
  blue: 'bg-blue-500',
  purple: 'bg-violet-500',
  green: 'bg-green-600',
  orange: 'bg-orange-500',
  red: 'bg-red-600',
  pink: 'bg-pink-500',
  teal: 'bg-teal-500',
}

const time = (wallClock: string) => wallClock.slice(11, 16)

function dayHeading(day: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${day}T12:00:00Z`))
}

/** The real name first, so a parent knows who teaches. A nickname alone hides it. */
function trainerName(trainer: MemberClass['trainer']): string | null {
  if (!trainer) return null
  const real = [trainer.first_name, trainer.last_name].filter(Boolean).join(' ').trim()
  if (real && trainer.nickname && real !== trainer.nickname) return `${real} (${trainer.nickname})`
  return real || trainer.nickname || null
}

function ClassRow({
  item,
  person,
  busy,
  onBook,
  onCancel,
}: {
  item: MemberClass
  person: ClassPerson
  busy: boolean
  onBook: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const state = item.people[person.key]
  const placesLeft = Math.max(0, item.capacity - item.booked)
  const full = placesLeft === 0
  const band =
    item.min_age !== null && item.max_age !== null
      ? `${item.min_age}–${item.max_age}`
      : item.min_age !== null
        ? `${item.min_age}+`
        : item.max_age !== null
          ? `≤ ${item.max_age}`
          : null
  const coach = trainerName(item.trainer)

  return (
    <SurfaceCard className="flex overflow-hidden">
      <div aria-hidden className={`w-1.5 shrink-0 ${COLOR_BAR[item.color] ?? COLOR_BAR.blue}`} />
      <div className="flex min-w-0 flex-1 items-center gap-3 p-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-xs font-medium tabular-nums text-foreground/60">
            {time(item.starts_at)}–{time(item.ends_at)}
          </p>
          <p className="truncate text-sm font-semibold">{item.title}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            {coach && (
              <p className="text-xs text-foreground/60">
                {t('classes.withTrainer', { name: coach })}
              </p>
            )}
            {band && (
              <Chip size="sm" variant="flat" className="h-5 text-[11px]">
                {t('classes.ageBand', { band })}
              </Chip>
            )}
            <Chip
              size="sm"
              variant="flat"
              color={full ? 'danger' : 'default'}
              className="h-5 text-[11px]"
            >
              {full ? t('classes.full') : t('classes.placesLeft', { count: placesLeft })}
            </Chip>
          </div>
          {!state.booked && state.blocked === 'no_plan' && (
            <p className="text-xs text-warning">{t('classes.planDoesNotCover')}</p>
          )}
          {!state.booked && state.blocked === 'no_entries' && (
            <p className="text-xs text-warning">
              {person.childId ? t('classes.childNoEntriesLeft') : t('classes.noEntriesLeft')}
            </p>
          )}
          {!state.booked && state.blocked === 'profile_dob' && (
            <p className="text-xs text-warning">
              {person.childId ? t('classes.childNeedsBirthDate') : t('classes.needsBirthDate')}
            </p>
          )}
        </div>
        {state.booked ? (
          <Button size="sm" variant="flat" color="success" isLoading={busy} onPress={onCancel}>
            {t('classes.bookedCancel')}
          </Button>
        ) : (
          <Button
            size="sm"
            color="primary"
            isLoading={busy}
            isDisabled={full || state.blocked !== null}
            onPress={onBook}
          >
            {t('classes.book')}
          </Button>
        )}
      </div>
    </SurfaceCard>
  )
}

export function ClassesPage() {
  const { t, i18n } = useTranslation()
  const { gymId } = useOutletContext<GymContext>()
  const { people, classes, loading, error, setBooked } = useMyClasses(gymId)
  const [personKey, setPersonKey] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const person = people.find((p) => p.key === personKey) ?? people[0] ?? null

  // Only the classes this person's age fits. The kids' group is not something
  // an adult needs to scroll past, and the other way round.
  const visible = useMemo(
    () => (person ? classes.filter((c) => c.people[person.key]?.blocked !== 'age') : []),
    [classes, person],
  )
  const days = useMemo(() => {
    const byDay = new Map<string, MemberClass[]>()
    for (const c of visible) {
      const day = c.starts_at.slice(0, 10)
      byDay.set(day, [...(byDay.get(day) ?? []), c])
    }
    return [...byDay.entries()]
  }, [visible])

  // Everything booked, for anyone on this account.
  const myBookings = useMemo(
    () =>
      classes.flatMap((c) =>
        people.filter((p) => c.people[p.key]?.booked).map((p) => ({ item: c, person: p })),
      ),
    [classes, people],
  )

  const personLabel = (p: ClassPerson) => (p.childId ? (p.firstName ?? '') : t('classes.me'))

  async function toggle(item: MemberClass, who: ClassPerson, book: boolean) {
    setBusyId(`${item.id}:${who.key}`)
    const code = await setBooked(item.id, who.childId, book)
    setBusyId(null)
    if (!code) {
      addToast({
        title: book ? t('classes.bookedToast') : t('classes.cancelledToast'),
        color: book ? 'success' : 'default',
      })
      return
    }
    addToast({
      title:
        code === 'CLASS_FULL'
          ? t('classes.errorFull')
          : code === 'PAST'
            ? t('classes.errorStarted')
            : code === 'NO_ENTRIES'
              ? t('classes.errorNoEntries')
              : code === 'NOT_ELIGIBLE'
                ? t('classes.errorNotEligible')
                : t('classes.errorGeneric'),
      color: 'danger',
    })
  }

  if (loading) {
    return (
      <Page>
        <SkeletonCard />
        <SkeletonCard />
      </Page>
    )
  }

  if (error || !person) {
    return (
      <Page>
        <EmptyState
          icon="🧗"
          title={t('classes.noPlanTitle')}
          description={t('classes.noPlanBody')}
        />
      </Page>
    )
  }

  return (
    <Page>
      <div className="flex flex-col gap-1 px-1">
        <h1 className="text-2xl font-bold">{t('classes.title')}</h1>
        <p className="text-sm text-foreground/60">{t('classes.subtitle')}</p>
      </div>

      {myBookings.length > 0 && (
        <Section title={t('classes.myBookings')}>
          <div className="flex flex-col gap-2">
            {myBookings.map(({ item, person: who }) => (
              <SurfaceCard key={`${item.id}:${who.key}`} className="flex items-center gap-3 p-3">
                <div className="flex min-w-0 flex-1 flex-col">
                  <p className="truncate text-sm font-semibold">{item.title}</p>
                  <p className="text-xs text-foreground/60">
                    {dayHeading(item.starts_at.slice(0, 10), i18n.language)} ·{' '}
                    {time(item.starts_at)}
                    {people.length > 1 ? ` · ${personLabel(who)}` : ''}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  isLoading={busyId === `${item.id}:${who.key}`}
                  onPress={() => void toggle(item, who, false)}
                >
                  {t('classes.cancel')}
                </Button>
              </SurfaceCard>
            ))}
          </div>
        </Section>
      )}

      {people.length > 1 && (
        <div className="flex items-center gap-2 px-1">
          <p className="text-sm text-foreground/60">{t('classes.bookFor')}</p>
          <ChipTabs
            aria-label={t('classes.bookFor')}
            options={people.map((p) => ({ key: p.key, label: personLabel(p) }))}
            value={person.key}
            onChange={setPersonKey}
          />
        </div>
      )}

      {days.length === 0 ? (
        <EmptyState
          icon="📅"
          title={t('classes.emptyTitle')}
          description={t('classes.emptyBody')}
        />
      ) : (
        days.map(([day, items]) => (
          <Section key={day} title={dayHeading(day, i18n.language)}>
            <div className="flex flex-col gap-2">
              {items.map((item, i) => (
                <motion.div key={item.id} {...staggerIndex(i)}>
                  <ClassRow
                    item={item}
                    person={person}
                    busy={busyId === `${item.id}:${person.key}`}
                    onBook={() => void toggle(item, person, true)}
                    onCancel={() => void toggle(item, person, false)}
                  />
                </motion.div>
              ))}
            </div>
          </Section>
        ))
      )}
    </Page>
  )
}
